import { useState } from "react";
import { AlertTriangle, Loader2, Wrench } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";

type Registro = { id: string; equipmentId: string | null; equipmentName: string };

/**
 * Que máquina foi usada neste trabalho.
 *
 * Mostra dois lados de propósito: o que a ficha mandava e o que se usou. A comparação é a
 * informação — quando divergem, alguém trocou de máquina no meio, e é isso que explica o gasto de
 * broca não bater com a receita.
 *
 * Trocar substitui as linhas de consumo daquela máquina, e só delas: broca lançada à mão e o resto
 * da ficha não se mexem.
 */
export function EquipamentoDoServico({
  serviceId,
  daFicha,
  usados,
  disponiveis,
  podeEditar,
}: {
  serviceId: string;
  daFicha: Registro[];
  usados: Registro[];
  disponiveis: Array<{ id: string; name: string }>;
  podeEditar: boolean;
}) {
  const utils = trpc.useUtils();
  const [confirmando, setConfirmando] = useState<string | null>(null);

  const definir = trpc.consumo.definirEquipamentos.useMutation({
    onSuccess: () => {
      toast.success("Equipamentos atualizados.");
      setConfirmando(null);
      utils.consumo.porServico.invalidate({ serviceId });
      utils.consumo.resumoDaProducao.invalidate();
      utils.estoque.invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao atualizar equipamentos."),
  });

  if (disponiveis.length === 0 && usados.length === 0 && daFicha.length === 0) return null;

  const idsUsados = usados.map((u) => u.equipmentId).filter(Boolean) as string[];
  const idsDaFicha = daFicha.map((f) => f.equipmentId).filter(Boolean) as string[];
  const divergiu =
    idsDaFicha.length > 0 &&
    (idsDaFicha.length !== idsUsados.length || idsDaFicha.some((id) => !idsUsados.includes(id)));

  const alternar = (id: string) => {
    const proximos = idsUsados.includes(id) ? idsUsados.filter((x) => x !== id) : [...idsUsados, id];
    // Tirar uma máquina apaga o consumo dela — mesma confirmação em dois cliques da troca de ficha.
    if (idsUsados.includes(id) && confirmando !== id) {
      setConfirmando(id);
      return;
    }
    definir.mutate({ serviceId, equipmentIds: proximos });
  };

  return (
    <div className="rounded-lg border border-zinc-200 p-3 dark:border-border">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-xs font-semibold text-zinc-700 dark:text-foreground/80">Equipamentos usados</p>
        {daFicha.length > 0 && (
          <p className="truncate text-[11px] text-zinc-500 dark:text-muted-foreground">
            A ficha assume: {daFicha.map((f) => f.equipmentName).join(", ")}
          </p>
        )}
      </div>

      {divergiu && (
        <p className="mt-1 flex items-start gap-1 text-[11px] text-amber-600 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          O que se usou não é o que a receita mandava — o gasto de consumível deste trabalho não bate
          com o da ficha.
        </p>
      )}

      {!podeEditar ? (
        <p className="mt-1.5 text-[11px] text-zinc-500 dark:text-muted-foreground">
          {usados.length > 0 ? usados.map((u) => u.equipmentName).join(", ") : "Nenhum registrado."}
        </p>
      ) : (
        <>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {disponiveis.map((e) => {
              const ativo = idsUsados.includes(e.id);
              const aConfirmar = confirmando === e.id;
              return (
                <button
                  key={e.id}
                  type="button"
                  disabled={definir.isPending}
                  onClick={() => alternar(e.id)}
                  className={cn(
                    "flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs transition-colors disabled:opacity-50",
                    aConfirmar
                      ? "border-red-300 bg-red-50 font-semibold text-red-700 dark:border-red-500/40 dark:bg-red-950/30 dark:text-red-400"
                      : ativo
                        ? "border-brand-300 bg-brand-50 font-semibold text-brand-700 dark:border-primary/40 dark:bg-primary/10 dark:text-primary"
                        : "border-zinc-200 bg-white text-zinc-600 hover:border-brand-300 dark:border-border dark:bg-card dark:text-muted-foreground",
                  )}
                >
                  <Wrench className="h-3.5 w-3.5 shrink-0 opacity-70" />
                  {aConfirmar ? `Tirar "${e.name}" e o gasto dela?` : e.name}
                </button>
              );
            })}
            {definir.isPending && <Loader2 className="h-4 w-4 animate-spin self-center text-brand-600" />}
          </div>
          {confirmando && (
            <button
              type="button"
              onClick={() => setConfirmando(null)}
              className="mt-1 text-[11px] text-zinc-500 underline dark:text-muted-foreground"
            >
              Cancelar
            </button>
          )}
          {/* Máquina que saiu do catálogo mas está no registro deste trabalho: aparece pra não
              sumir da história, sem botão, porque não dá pra escolher de novo. */}
          {usados
            .filter((u) => !u.equipmentId || !disponiveis.some((d) => d.id === u.equipmentId))
            .map((u) => (
              <p key={u.id} className="mt-1 text-[11px] text-zinc-500 dark:text-muted-foreground">
                {u.equipmentName} (fora do catálogo)
              </p>
            ))}
        </>
      )}
    </div>
  );
}
